/**
 * Single source of truth for bout storage: `matches_canonical/{deterministicId}`.
 * Id algorithm must match `functions/matchCanonicalMigration.js`.
 */

import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";

export const MATCHES_CANONICAL_COLLECTION = "matches_canonical";

export function matchDateMillis(matchDate) {
  if (!matchDate) return 0;
  if (typeof matchDate.toMillis === "function") return matchDate.toMillis();
  if (matchDate.seconds != null) {
    return (
      matchDate.seconds * 1000 + Math.floor((matchDate.nanoseconds || 0) / 1e6)
    );
  }
  return 0;
}

/**
 * @param {string} winnerId
 * @param {string} loserId
 * @param {import("firebase/firestore").Timestamp | Date} matchDate
 * @param {string} type
 */
export async function computeCanonicalMatchDocId(
  winnerId,
  loserId,
  matchDate,
  type
) {
  const ms = matchDateMillis(matchDate);
  const t = String(type || "").trim();
  const key = `${winnerId}|${loserId}|${ms}|${t}`;
  if (
    typeof crypto === "undefined" ||
    !crypto.subtle ||
    typeof crypto.subtle.digest !== "function"
  ) {
    throw new Error(
      "SHA-256 is not available (HTTPS required). Cannot compute canonical match id."
    );
  }
  const enc = new TextEncoder().encode(key);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  const hex = Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hex.slice(0, 32);
}

export function participantAthleteIds(winnerId, loserId) {
  return [winnerId, loserId].filter(Boolean);
}

/** Stable string id for comparing athlete ids across string/number/DocumentReference. */
export function canonicalAthleteIdKey(x) {
  if (x == null || x === "") return "";
  if (typeof x === "object" && x && typeof x.path === "string") {
    const segs = x.path.split("/");
    return segs.length ? String(segs[segs.length - 1] || "").trim() : "";
  }
  return String(x).trim();
}

/**
 * Values to use in Firestore `==` / `array-contains` (types must match stored field).
 * @param {string|number} athleteId
 * @returns {(string|number)[]}
 */
export function canonicalAthleteIdEqualityVariants(athleteId) {
  const s = String(athleteId ?? "").trim();
  if (!s) return [];
  const out = [];
  const seen = new Set();
  const push = (v) => {
    const key = `${typeof v}\0${String(v)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(v);
  };
  push(s);
  const n = Number(s);
  if (Number.isFinite(n) && !Number.isNaN(n)) push(n);
  return out;
}

/**
 * Per-athlete row shape expected by list UIs (ResultList, Wrestler, ranking H2H, etc.).
 * @param {object} data - raw `matches_canonical` document
 * @param {string} viewerAthleteId
 * @returns {object | null}
 */
export function perspectiveFromCanonical(data, viewerAthleteId) {
  if (!data || viewerAthleteId == null || viewerAthleteId === "") return null;
  const v = canonicalAthleteIdKey(viewerAthleteId);
  if (!v) return null;
  const w = canonicalAthleteIdKey(data.WinnerAthleteId);
  const l = canonicalAthleteIdKey(data.LoserAthleteId);
  if (w && v === w) {
    return {
      Location: data.Location != null ? data.Location : "",
      MatchDate: data.MatchDate,
      Season: data.Season != null ? data.Season : "",
      OpponentFullName:
        data.OpponentLabelWinnerDoc != null ? data.OpponentLabelWinnerDoc : "",
      OpponentImage: "",
      Result: "Win",
      Type: data.Type != null ? data.Type : "",
      VideoLink: data.VideoLink != null ? data.VideoLink : "",
      WrestlerID:
        l || (data.LoserAthleteId != null ? String(data.LoserAthleteId) : ""),
      SkillChange:
        typeof data.SkillChangeWinner === "number" ? data.SkillChangeWinner : 0,
    };
  }
  if (l && v === l) {
    return {
      Location: data.Location != null ? data.Location : "",
      MatchDate: data.MatchDate,
      Season: data.Season != null ? data.Season : "",
      OpponentFullName:
        data.OpponentLabelLoserDoc != null ? data.OpponentLabelLoserDoc : "",
      OpponentImage: "",
      Result: "Loss",
      Type: data.Type != null ? data.Type : "",
      VideoLink: data.VideoLink != null ? data.VideoLink : "",
      WrestlerID:
        w || (data.WinnerAthleteId != null ? String(data.WinnerAthleteId) : ""),
      SkillChange:
        typeof data.SkillChangeLoser === "number" ? data.SkillChangeLoser : 0,
    };
  }
  return null;
}

export async function canonicalMatchDocExists(db, canonicalId) {
  const ref = doc(db, MATCHES_CANONICAL_COLLECTION, canonicalId);
  const snap = await getDoc(ref);
  return snap.exists();
}

/** True if a bout already exists for this pair, date, and type (either winner). */
export async function duplicateBoutEitherWinner(
  db,
  athleteA,
  athleteB,
  matchDate,
  type
) {
  const [id1, id2] = await Promise.all([
    computeCanonicalMatchDocId(athleteA, athleteB, matchDate, type),
    computeCanonicalMatchDocId(athleteB, athleteA, matchDate, type),
  ]);
  const [e1, e2] = await Promise.all([
    getDoc(doc(db, MATCHES_CANONICAL_COLLECTION, id1)),
    getDoc(doc(db, MATCHES_CANONICAL_COLLECTION, id2)),
  ]);
  const ex = (s) => (typeof s.exists === "function" ? s.exists() : s.exists);
  return ex(e1) || ex(e2);
}

/**
 * @param {import("firebase/firestore").CollectionReference} ref
 * @param {string} field
 * @param {string|number} idVal
 * @param {number} n
 */
async function queryCanonicalSideNewestFirst(ref, field, idVal, n) {
  try {
    const snap = await getDocs(
      query(
        ref,
        where(field, "==", idVal),
        orderBy("MatchDate", "desc"),
        limit(n)
      )
    );
    return snap.docs;
  } catch (_e) {
    const snap = await getDocs(query(ref, where(field, "==", idVal)));
    const docs = snap.docs
      .slice()
      .sort(
        (a, b) =>
          matchDateMillis(b.data().MatchDate) -
          matchDateMillis(a.data().MatchDate)
      );
    return docs.slice(0, n);
  }
}

/**
 * @param {import("firebase/firestore").CollectionReference} ref
 * @param {string|number} idVal
 * @param {number} n
 */
async function queryParticipantContainsNewestFirst(ref, idVal, n) {
  try {
    const snap = await getDocs(
      query(
        ref,
        where("ParticipantAthleteIds", "array-contains", idVal),
        orderBy("MatchDate", "desc"),
        limit(n)
      )
    );
    return snap.docs;
  } catch (_e) {
    const snap = await getDocs(
      query(ref, where("ParticipantAthleteIds", "array-contains", idVal))
    );
    const docs = snap.docs
      .slice()
      .sort(
        (a, b) =>
          matchDateMillis(b.data().MatchDate) -
          matchDateMillis(a.data().MatchDate)
      );
    return docs.slice(0, n);
  }
}

/**
 * Bouts involving this athlete, newest first (perspective rows).
 * Uses WinnerAthleteId / LoserAthleteId; tries string and numeric `==` (Firestore types);
 * falls back to equality-only queries if composite index is missing; then ParticipantAthleteIds.
 */
export async function fetchMergedCanonicalMatchRowsDesc(
  db,
  athleteId,
  maxPerSide
) {
  const primaryId = String(athleteId ?? "").trim();
  if (!primaryId) return [];
  const ref = collection(db, MATCHES_CANONICAL_COLLECTION);
  const n = Math.min(Math.max(Number(maxPerSide) || 0, 1), 5000);
  const variants = canonicalAthleteIdEqualityVariants(athleteId);
  const byId = new Map();

  const sideQueries = [];
  for (const v of variants) {
    sideQueries.push(
      queryCanonicalSideNewestFirst(ref, "WinnerAthleteId", v, n)
    );
    sideQueries.push(
      queryCanonicalSideNewestFirst(ref, "LoserAthleteId", v, n)
    );
  }
  const sideChunks = await Promise.all(sideQueries);
  for (const docs of sideChunks) {
    for (const d of docs) byId.set(d.id, d);
  }

  if (byId.size === 0) {
    const partQueries = variants.map((v) =>
      queryParticipantContainsNewestFirst(ref, v, n)
    );
    const partChunks = await Promise.all(partQueries);
    for (const docs of partChunks) {
      for (const d of docs) byId.set(d.id, d);
    }
  }

  const docs = Array.from(byId.values()).sort(
    (a, b) =>
      matchDateMillis(b.data().MatchDate) - matchDateMillis(a.data().MatchDate)
  );
  const rows = [];
  for (const d of docs) {
    const data = perspectiveFromCanonical(d.data(), primaryId);
    if (data) rows.push({ id: d.id, data });
  }
  return rows;
}

/**
 * Newest-first perspective rows for one athlete where Firestore `Season` is in `seasonStoredVariants`.
 */
export async function fetchPerspectiveRowsForAthleteSeasonDesc(
  db,
  athleteId,
  seasonStoredVariants,
  maxEach
) {
  const primaryId = String(athleteId ?? "").trim();
  const sv = [...new Set((seasonStoredVariants || []).filter(Boolean))].slice(
    0,
    10
  );
  if (!primaryId || !sv.length) return [];
  const ref = collection(db, MATCHES_CANONICAL_COLLECTION);
  const n = Math.min(Math.max(Number(maxEach) || 0, 1), 50);
  const idVars = canonicalAthleteIdEqualityVariants(athleteId);
  const byId = new Map();

  const querySeasonSide = async (field, idVal) => {
    try {
      const snap = await getDocs(
        query(
          ref,
          where(field, "==", idVal),
          where("Season", "in", sv),
          orderBy("MatchDate", "desc"),
          limit(n)
        )
      );
      return snap.docs;
    } catch (_e) {
      try {
        const snap = await getDocs(
          query(
            ref,
            where(field, "==", idVal),
            where("Season", "in", sv),
            limit(n)
          )
        );
        return snap.docs;
      } catch (_e2) {
        return [];
      }
    }
  };

  for (const idVal of idVars) {
    const [w, l] = await Promise.all([
      querySeasonSide("WinnerAthleteId", idVal),
      querySeasonSide("LoserAthleteId", idVal),
    ]);
    for (const d of [...w, ...l]) byId.set(d.id, d);
  }

  if (byId.size === 0) {
    for (const idVal of idVars) {
      try {
        const snap = await getDocs(
          query(
            ref,
            where("ParticipantAthleteIds", "array-contains", idVal),
            where("Season", "in", sv),
            orderBy("MatchDate", "desc"),
            limit(n)
          )
        );
        for (const d of snap.docs) byId.set(d.id, d);
      } catch (_e) {
        try {
          const snap = await getDocs(
            query(
              ref,
              where("ParticipantAthleteIds", "array-contains", idVal),
              where("Season", "in", sv),
              limit(n)
            )
          );
          for (const d of snap.docs) byId.set(d.id, d);
        } catch (_e2) {
          /* noop */
        }
      }
    }
  }

  const merged = Array.from(byId.values()).sort(
    (a, b) =>
      matchDateMillis(b.data().MatchDate) - matchDateMillis(a.data().MatchDate)
  );
  const rows = [];
  for (const d of merged.slice(0, n)) {
    const data = perspectiveFromCanonical(d.data(), primaryId);
    if (data) rows.push(data);
  }
  return rows;
}

export async function fetchCanonicalMatchesTimeline(db, athleteId, lim) {
  const maxPerSide = Math.max(lim, 1);
  const rowsDesc = await fetchMergedCanonicalMatchRowsDesc(
    db,
    athleteId,
    maxPerSide
  );
  const top = rowsDesc.slice(0, lim);
  return top.slice().reverse();
}

/** Oldest → newest perspective rows (skill replay on match page). */
export async function fetchPerspectiveMatchesChronological(db, athleteId) {
  const rowsDesc = await fetchMergedCanonicalMatchRowsDesc(db, athleteId, 2500);
  return rowsDesc
    .slice()
    .reverse()
    .map((r) => ({ id: r.id, ...r.data }));
}

/** All bouts for an athlete as `{ id, data }` list (newest-first merge). */
export async function fetchAllPerspectiveRowsForAthlete(db, athleteId) {
  return fetchMergedCanonicalMatchRowsDesc(db, athleteId, 2000);
}

export async function countWinsLossesFromCanonical(db, athleteId) {
  const rows = await fetchMergedCanonicalMatchRowsDesc(db, athleteId, 8000);
  let wins = 0;
  let losses = 0;
  for (const r of rows) {
    if (r.data.Result === "Win") wins += 1;
    else if (r.data.Result === "Loss") losses += 1;
  }
  return { wins, losses };
}

/**
 * All canonical bouts where both athletes are in `idSet` (league-scoped replay / delete).
 * @param {Set<string> | string[]} ids
 */
export async function fetchCanonicalBoutsForAthleteSet(db, ids) {
  const idSet = ids instanceof Set ? ids : new Set(ids || []);
  const idList = [...idSet].filter(Boolean);
  const byId = new Map();
  const ref = collection(db, MATCHES_CANONICAL_COLLECTION);
  for (const aid of idList) {
    const [snapW, snapL] = await Promise.all([
      getDocs(query(ref, where("WinnerAthleteId", "==", aid))),
      getDocs(query(ref, where("LoserAthleteId", "==", aid))),
    ]);
    for (const d of [...snapW.docs, ...snapL.docs]) {
      const data = d.data() || {};
      const w = data.WinnerAthleteId;
      const l = data.LoserAthleteId;
      if (idSet.has(w) && idSet.has(l)) {
        byId.set(d.id, { id: d.id, ref: d.ref, data });
      }
    }
  }
  return Array.from(byId.values());
}

/** Full scan, oldest first (global admin replay). */
export async function fetchAllCanonicalMatchDocsChronological(db) {
  const ref = collection(db, MATCHES_CANONICAL_COLLECTION);
  const pageSize = 300;
  const out = [];
  let cursor = null;
  for (;;) {
    let q = query(
      ref,
      orderBy("MatchDate", "asc"),
      orderBy(documentId()),
      limit(pageSize)
    );
    if (cursor) {
      q = query(
        ref,
        orderBy("MatchDate", "asc"),
        orderBy(documentId()),
        startAfter(cursor),
        limit(pageSize)
      );
    }
    const snap = await getDocs(q);
    if (snap.empty) break;
    out.push(...snap.docs);
    cursor = snap.docs[snap.docs.length - 1];
    if (snap.size < pageSize) break;
  }
  return out;
}
